import React, { useContext, useMemo } from "react";
import { APP_PAGE, actions } from "../../hooks/useAppState";
import useT from "../../hooks/useT";
import useNotifications from "../../hooks/useNotifications";
import Button from "../../components/Button";
import AppFrame from "../../components/AppFrame";
import AppLanguageSwitcher from "../../containers/AppLanguageSwitcher";
import { AppStateContext } from "../App";
import api from "../../utils/api";

export interface AppLayoutProps {
  lang?: string;
  headerLeftContent?: React.ReactNode | React.ReactNodeArray;
  headerRightContent?: React.ReactNode | React.ReactNodeArray;
  footerContent?: React.ReactNode | React.ReactNodeArray;
  children?: React.ReactNode | React.ReactNodeArray;
}

export interface AppLayoutChildProps {
  lang?: string;
}

export function AppLayoutHeaderLeft({ lang }: AppLayoutChildProps) {
  const { t } = useT();
  const [appState, doAppStateAction] = useContext(AppStateContext);
  const config = appState.config;
  return (
    <>
      {config.customise.header.showSetup && (
        <Button
          className="action-setup"
          icon="setup"
          label={t("actions.setupApp.label", { lng: lang })}
          onClick={event => {
            event.preventDefault();
            // reset salesPersonId to enforce re-enter it before setup
            doAppStateAction(actions.setSalesPerson(""));
            // go to setup page
            doAppStateAction(actions.setPage(APP_PAGE.SETUP));
          }}
        />
      )}
    </>
  );
}

export function AppLayoutHeaderRight({ lang }: AppLayoutChildProps) {
  // Shared contexts/state
  const { t } = useT();
  const [appState, doAppStateAction] = useContext(AppStateContext);
  const { checkNotifications, notificationsStore } = useNotifications();
  const config = appState.config;

  // Memoised values
  const notificationIcon = useMemo(() => {
    const unreadNotifications = notificationsStore.getByProp("read", false);
    if (unreadNotifications) {
      return "notification-unread";
    } else {
      return "notification";
    }
  }, [checkNotifications]);

  return (
    <>
      {config.customise.header.showLogout && (
        <Button
          className="action-logout"
          icon="logout"
          onClick={(event: React.MouseEvent) => {
            event.preventDefault();
            api.logout();
          }}
        />
      )}
      {config.customise.header.showNotifications && (
        <Button
          className="action-notifications"
          icon={notificationIcon}
          label={t("actions.showNotifications.label", { lng: lang })}
          onClick={(event: React.MouseEvent) => {
            event.preventDefault();
            // reset salesPersonId to enforce re-enter it before setup
            doAppStateAction(actions.setSalesPerson(""));
            // go to notifications page
            doAppStateAction(actions.setPage(APP_PAGE.NOTIFICATIONS));
          }}
        />
      )}
      {config.customise.header.showLanguage && <AppLanguageSwitcher />}
    </>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function AppLayoutFooter({ lang }: AppLayoutChildProps) {
  const [appState] = useContext(AppStateContext);
  const config = appState.config;
  return config.customise.footer.showLanguage ? <AppLanguageSwitcher /> : null;
}

export default function AppLayout({
  lang,
  headerLeftContent,
  headerRightContent,
  footerContent,
  children
}: AppLayoutProps) {
  const [appState] = useContext(AppStateContext);
  const lng = lang || appState.language;
  return (
    <AppFrame
      headerLeftContent={
        headerLeftContent || <AppLayoutHeaderLeft lang={lng} />
      }
      headerRightContent={
        headerRightContent || <AppLayoutHeaderRight lang={lng} />
      }
      footerContent={footerContent || <AppLayoutFooter lang={lng} />}
    >
      {children}
    </AppFrame>
  );
}
